import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackLink from "../components/BackLink";
import { api, recordVisit } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Mic, Lock } from "lucide-react";
import MadaniPage from "../components/MadaniPage";
import { useLanguage } from "../i18n/LanguageContext";
import { useBasicHifz } from "@/lib/hifzScope";

const STATUSES = [
  { value: "not_started", label: "Not started", cls: "status-not-started" },
  { value: "learning", label: "Learning", cls: "status-learning" },
  { value: "memorized", label: "Memorized", cls: "status-memorized" },
  { value: "revising", label: "Revising", cls: "status-revising" },
];

// Every surah has Madani (page-glyph) data now (all 604 pages); a surah only falls back to the flowing-text
// rendering below when some of its ayahs are outside the learner's plan (hiddenCount).
const MADANI_SURAHS = Array.from({ length: 114 }, (_, i) => i + 1);

// Every ayah opens the SAME guided flow (Read → Listen & Repeat → Understand →
// Reflect → Record → AI Feedback → Mark Memorized) at /app/memorize — tapping
// an ayah (word, on the Madani page; the whole block, on the flowing-text
// fallback) is now the only way in, no separate button per ayah.
export default function SurahView() {
  const { tx, t: tr } = useLanguage();
  const { surah } = useParams();
  const navigate = useNavigate();
  const hs = useBasicHifz();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState({}); // {ayah: status}
  const [pageRange, setPageRange] = useState(null); // [firstPage, lastPage]
  const [page, setPage] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/quran/surahs/${surah}`).then((r) => setData(r.data)),
      api.get("/hifz/progress").then((r) => {
        const map = {};
        r.data.filter((p) => p.surah === parseInt(surah)).forEach((p) => { map[p.ayah] = p.status; });
        setProgress(map);
      }),
    ]).catch(() => toast.error(tr("surahView.couldnTLoadSurah")));
    const t = setTimeout(() => recordVisit({
      kind: "hifz", surah: parseInt(surah), ayah: 1, path: `/app/hifz/${surah}`,
    }), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surah]);

  // For Madani-eligible surahs, resolve which mushaf page(s) this surah's
  // (v1-scoped) ayahs land on, so we can render the authentic page instead
  // of a flowing verse list.
  useEffect(() => {
    setPageRange(null);
    setPage(null);
    if (!MADANI_SURAHS.includes(parseInt(surah)) || !data?.ayahs?.length) return;
    const first = data.ayahs[0].n;
    const last = data.ayahs[data.ayahs.length - 1].n;
    Promise.all([
      api.get(`/quran/ayah-page/${surah}/${first}`).catch(() => null),
      api.get(`/quran/ayah-page/${surah}/${last}`).catch(() => null),
    ]).then(([a, b]) => {
      const p1 = a?.data?.page;
      const p2 = b?.data?.page || p1;
      if (p1) { setPageRange([p1, p2]); setPage(p1); }
    });
  }, [surah, data]);

  if (!data) {
    return (
      <div className="space-y-6 animate-pulse-soft" data-testid="surah-view-skeleton">
        <div className="h-4 w-28 bg-sand-200 rounded" />
        <div className="hifz-card grain bg-forest-700/90 text-sand-100 border-forest-700">
          {/* Matches the real header's items-center (see C6 fix below) so
              there's no alignment jump when the actual content arrives. */}
          <div className="flex items-center justify-between gap-6">
            <div className="space-y-2 flex-1">
              <div className="h-3 w-32 bg-sand-100/20 rounded" />
              <div className="h-9 w-64 bg-sand-100/25 rounded" />
              <div className="h-4 w-40 bg-sand-100/15 rounded" />
            </div>
            <div className="h-10 w-24 bg-sand-100/20 rounded" />
          </div>
        </div>
        <div className="hifz-card h-96 bg-sand-200/50" />
        <p className="text-center text-xs text-muted-foreground">{tr("surahView.fetchingSurahFromTheMushaf")}</p>
      </div>
    );
  }

  // The Free plan works on Basic Hifz only: the ayahs outside it stay out of reach (the server refuses them too).
  const goMemorize = (ayahN) => {
    if (!hs.inScope(data.number, ayahN)) { toast.info(tr("hifzTest.outsidePlan")); return; }
    navigate(`/app/memorize/${data.number}/${ayahN}`);
  };
  const visibleAyahs = data.ayahs.filter((a) => hs.inScope(data.number, a.n));
  const hiddenCount = data.ayahs.length - visibleAyahs.length;
  // The Mushaf page shows (and lets you tap) every ayah on it, so it is only used when the whole surah is in the plan.
  const useMadani = MADANI_SURAHS.includes(data.number) && pageRange && page && hiddenCount === 0;

  return (
    <div className="space-y-6" data-testid="surah-view-page">
      <BackLink to="/app/hifz" label={tr("surahView.allSurahs")} testid="back-to-plan" />

      {useMadani ? (
        <div className="space-y-4">
          <p className="text-center text-xs text-muted-foreground">
            {tr("surahView.tapAnAyahSwipe")}
            {pageRange[0] !== pageRange[1] && <> {tr("surahView.page", { page })}</>}
          </p>
          {/* Floating arrows over the page's own left/right edges, matching
              the My Quran reader — Mushaf pages turn right-to-left, so
              "forward" moves left on screen: the LEFT arrow advances the
              page number, the RIGHT arrow goes back. */}
          <div className="relative">
            {pageRange[0] !== pageRange[1] && (
              <div className="pointer-events-none sticky top-[50vh] z-30 h-0">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageRange[1], p + 1))}
                  disabled={page >= pageRange[1]}
                  aria-label={tr("myQuran.nextPage")}
                  className="pointer-events-auto absolute left-1 -translate-y-1/2 h-7 w-7 rounded-full bg-forest-700/85 hover:bg-forest-700 text-sand-100 shadow grid place-items-center backdrop-blur-sm transition disabled:opacity-0 disabled:pointer-events-none"
                  data-testid="surah-page-next"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(pageRange[0], p - 1))}
                  disabled={page <= pageRange[0]}
                  aria-label={tr("myQuran.previousPage")}
                  className="pointer-events-auto absolute right-1 -translate-y-1/2 h-7 w-7 rounded-full bg-forest-700/85 hover:bg-forest-700 text-sand-100 shadow grid place-items-center backdrop-blur-sm transition disabled:opacity-0 disabled:pointer-events-none"
                  data-testid="surah-page-prev"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
            <MadaniPage
              pageNo={page}
              wordsClickable
              readerMode
              onWordClick={(verseKey) => goMemorize(parseInt(verseKey.split(":")[1]))}
              onSwipeNext={() => setPage((p) => Math.min(pageRange[1], p + 1))}
              onSwipePrev={() => setPage((p) => Math.max(pageRange[0], p - 1))}
            />
          </div>
        </div>
      ) : (
        <div className="hifz-card space-y-1" data-testid="surah-flow-text">
          {visibleAyahs.map((a) => {
            const status = progress[a.n] || "not_started";
            return (
              <div
                key={a.n}
                role="button"
                tabIndex={0}
                onClick={() => goMemorize(a.n)}
                onKeyDown={(e) => e.key === "Enter" && goMemorize(a.n)}
                className="py-4 border-b border-sand-200 last:border-0 cursor-pointer hover:bg-gold-500/15 rounded-lg px-2 -mx-2 transition"
                data-testid={`ayah-${a.n}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{tr("surahView.ayah", { n: a.n })}</span>
                  <span className={`status-pill ${STATUSES.find((s) => s.value === status)?.cls}`}>
                    {tx(STATUSES.find((s) => s.value === status)?.label)}
                  </span>
                </div>
                <div className="ayah-arabic text-3xl md:text-4xl mt-3 text-forest-700" dir="rtl">{a.ar}</div>
                <div className="mt-3 text-sm text-muted-foreground italic">{a.translit}</div>
                <div className="mt-1.5 text-foreground/85 leading-relaxed">{a.en}</div>
              </div>
            );
          })}
          {hiddenCount > 0 && (
            <div className="flex items-center gap-3 rounded-xl bg-gold-500/5 border border-gold-500/40 px-3.5 py-3 mt-3" data-testid="surah-locked-rest">
              <Lock className="h-4 w-4 text-gold-600 shrink-0" />
              <span className="flex-1 text-sm text-foreground/80">{tr("basicHifz.moreLocked", { count: hiddenCount })}</span>
              <button type="button" onClick={() => navigate("/app/basic-hifz")} className="btn-accent text-xs shrink-0" data-testid="surah-see-steps">{tr("basicHifz.seeSteps")}</button>
            </div>
          )}
        </div>
      )}

      {/* Floating shortcut into the Hifz Test — anchored to the app frame
          (absolute, not fixed/inline) so it floats above the scrolling
          mushaf/ayah list without ever sitting on top of the Arabic text
          itself; same placement convention as My Quran's Tadabbur FAB. */}
      <button
        type="button"
        onClick={() => navigate("/app/hifz-test")}
        className="absolute bottom-[calc(var(--mobile-tabbar-h)_+_env(safe-area-inset-bottom)_+_1rem)] right-6 z-40 h-12 w-12 rounded-full bg-forest-700 text-sand-100 shadow-lg grid place-items-center hover:bg-forest-600 transition"
        title={tr("hifzPlan.startAHifzTest")}
        aria-label={tr("hifzPlan.startAHifzTest")}
        data-testid="surah-hifz-test-fab"
      >
        <Mic className="h-5 w-5" />
      </button>
    </div>
  );
}
